import CancelIcon from "@mui/icons-material/Cancel";
import CancelScheduleSendIcon from "@mui/icons-material/CancelScheduleSend";
import EditIcon from "@mui/icons-material/Edit";
import FeedIcon from "@mui/icons-material/Feed";
import PrintIcon from "@mui/icons-material/Print";
import ReceiptIcon from "@mui/icons-material/Receipt";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Menu } from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";

import { ORDER_PERMISSIONS } from "@/constants/modules.permissions";
import ROUTE_URLS from "@/constants/route-urls";
import useLanguage from "@/contexts/language-context";
import useHasNoPermission from "@/hooks/use-check-permissions";
import { IOrder } from "@/interfaces/order.interfaces";
import { OrdersTableMessages } from "@/messages/orders/orders-table.messages";
import NotificationItem from "@/mui/Items/NotificationItem";
import { OrderStatus } from "@/types/order.types";

interface Props {
  isOpen: boolean;
  anchorEl: (EventTarget & HTMLButtonElement) | null;
  onClose: () => void;
  messages: OrdersTableMessages;
  selected: IOrder;
  onCancel: (order: IOrder) => void;
}

export function OrderPopover({ isOpen, anchorEl, onClose, messages, selected, onCancel }: Props) {
  const router = useNavigate();
  const { lang } = useLanguage();

  const hasNoPermissionToDelete = useHasNoPermission(ORDER_PERMISSIONS.DELETE);
  const hasNoPermissionToEdit = useHasNoPermission(ORDER_PERMISSIONS.EDIT);
  const hasNoPermissionToPrint = useHasNoPermission(ORDER_PERMISSIONS.PRINT);
  const hasNoPermissionToView = useHasNoPermission(ORDER_PERMISSIONS.VIEW);

  const { updatePopover, cancelPopover, printPopover, viewPopover } = messages;

  const isCancelStatus =
    selected.orderStatus === OrderStatus.Cancelled ||
    selected.orderStatus === OrderStatus.CancellationRequest;
  const isDeliveredStatus = selected.orderStatus === OrderStatus.Delivered;
  const canShowCancel = !hasNoPermissionToDelete && !isCancelStatus && !isDeliveredStatus;

  const paymentReceiptUrl = selected.payments?.[0]?.transactionReceiptUrl || null;
  const cancellationReceiptUrl = selected.payments?.[0]?.cancellationReceiptUrl || null;
  const refundFormUrl = selected.payments?.[0]?.refundFormUrl || null;

  return (
    <Menu
      open={isOpen}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
    >
      <NotificationItem
        icon={<EditIcon />}
        disabled={hasNoPermissionToEdit}
        onClick={() => router(ROUTE_URLS.orderEdit(lang, selected.id.toString()))}
        title={updatePopover}
      />

      {canShowCancel && (
        <NotificationItem
          icon={<CancelIcon />}
          disabled={false}
          onClick={() => onCancel(selected)}
          title={cancelPopover}
        />
      )}

      <NotificationItem
        icon={<PrintIcon />}
        disabled={hasNoPermissionToPrint}
        onClick={() => router(ROUTE_URLS.orderPrint(lang, selected.id.toString()))}
        title={printPopover}
      />

      <NotificationItem
        icon={<VisibilityIcon />}
        disabled={hasNoPermissionToView}
        onClick={() => router(ROUTE_URLS.orderDetails(lang, selected.id.toString()))}
        title={viewPopover}
      />

      {paymentReceiptUrl && (
        <NotificationItem
          icon={<ReceiptIcon />}
          disabled={false}
          onClick={() => window.open(paymentReceiptUrl, "_blank")}
          title={messages.paymentReceiptTitle}
        />
      )}

      {cancellationReceiptUrl && (
        <NotificationItem
          icon={<CancelScheduleSendIcon />}
          disabled={false}
          onClick={() => window.open(cancellationReceiptUrl, "_blank")}
          title={messages.cancellationReceiptTitle}
        />
      )}

      {refundFormUrl && (
        <NotificationItem
          icon={<FeedIcon />}
          disabled={false}
          onClick={() => window.open(refundFormUrl, "_blank")}
          title={messages.cancellationFormTitle}
        />
      )}
    </Menu>
  );
}
